import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaUndo, FaRupeeSign } from "react-icons/fa";
import toast from "react-hot-toast";

import { useAddSalesReturnMutation } from "../../toolkitServices/salesReturnApi";
import { useGetInvoiceByIdQuery } from "../../toolkitServices/invoiceApi";

/* ------------ Tiny spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ------------ Helpers ------------ */
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatAmount = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getInvoiceGrandTotal = (inv) => {
  if (!inv) return 0;
  if (inv.totals?.grandTotal != null) return Number(inv.totals.grandTotal) || 0;
  return (
    Number(
      inv.invoiceAmount ??
        inv.totalAmount ??
        inv.grandTotal ??
        inv.netAmount ??
        0
    ) || 0
  );
};

const getInvoicePaid = (inv) => {
  if (!inv) return 0;
  if (inv.totals?.paid != null) return Number(inv.totals.paid) || 0;
  return Number(inv.paidAmount ?? inv.invoicePaid ?? 0) || 0;
};

const getInvoiceOutstanding = (inv) => {
  if (!inv) return 0;
  if (inv.totals?.outstanding != null)
    return Number(inv.totals.outstanding) || 0;
  return (
    Number(
      inv.invoiceOutstanding ??
        inv.outstanding ??
        inv.balanceAmount ??
        0
    ) || 0
  );
};

const makeItemKey = (e) =>
  e?.labelledItemId ||
  e?.invoiceItemCode ||
  e?.itemCode ||
  `${e?.productId || ""}-${e?.designId || ""}-${e?.purityId || ""}`;

const getItemQty = (e) => Number(e?.qty ?? e?.quantity ?? 1) || 0;

/* ====================================================================== */
/*                       MODAL: CREATE SALES RETURN                       */
/* ====================================================================== */

const CreateSalesReturnModal = ({
  open,
  onClose,
  invoice, // summary row (list se aaya), sirf id/code bhi ho sakta hai
  customer, // optional: same option object as CustomerTransactions
  onCreated, // callback(result) after successful save
}) => {
  const [mode, setMode] = useState("Cash"); // refund mode
  const [charges, setCharges] = useState("");
  const [note, setNote] = useState("");
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  const [addSalesReturn, { isLoading: isSaving }] = useAddSalesReturnMutation();

  // ------- Invoice ID from prop -------
  const invoiceId =
    invoice?._id ||
    invoice?.id ||
    invoice?.invoiceId ||
    invoice?.sourceId ||
    null;

  // ------- Fetch full invoice (with stockEntries) by ID -------
  const { data: invoiceDetailRes, isLoading: isFetchingInvoice } =
    useGetInvoiceByIdQuery(invoiceId, {
      skip: !invoiceId || !open,
    });

  // RTK response shape flexible: {data: {...}} or direct doc
  const fullInvoice = useMemo(() => {
    if (!invoiceDetailRes) return null;
    if (invoiceDetailRes.data && !invoiceDetailRes.data.data)
      return invoiceDetailRes.data;
    if (invoiceDetailRes.data?.data) return invoiceDetailRes.data.data;
    return invoiceDetailRes;
  }, [invoiceDetailRes]);

  // Effective invoice: prefer fullInvoice (with items), fallback summary invoice
  const effectiveInvoice = fullInvoice || invoice || {};
  const returnedQtyByKey =
    effectiveInvoice?.__returnedQtyByKey || invoice?.__returnedQtyByKey || {};

  const grandTotal = useMemo(
    () => getInvoiceGrandTotal(effectiveInvoice),
    [effectiveInvoice]
  );
  const paidTotal = useMemo(
    () => getInvoicePaid(effectiveInvoice),
    [effectiveInvoice]
  );
  const outstanding = useMemo(
    () => getInvoiceOutstanding(effectiveInvoice),
    [effectiveInvoice]
  );

  // ✅ FIX: ONLY FULLY PAID invoices allowed for sales return
  const isFullyPaid = outstanding <= 0.009;

  // Invoice items (prefer stockEntries, fallback to items)
  const rawInvoiceItems = useMemo(
    () =>
      (effectiveInvoice?.stockEntries &&
      Array.isArray(effectiveInvoice.stockEntries)
        ? effectiveInvoice.stockEntries
        : Array.isArray(effectiveInvoice?.items)
        ? effectiveInvoice.items
        : []) || [],
    [effectiveInvoice]
  );

  // ✅ Hide fully-returned items
  const invoiceItems = useMemo(() => {
    const list = rawInvoiceItems || [];
    if (!list.length) return [];

    // If no returned map, return as-is
    if (!returnedQtyByKey || Object.keys(returnedQtyByKey).length === 0)
      return list;

    return list.filter((it) => {
      const key = makeItemKey(it);
      const invQty = getItemQty(it);
      const retQty = Number(returnedQtyByKey?.[key] || 0) || 0;
      const remaining = invQty - retQty;
      return remaining > 0.0001;
    });
  }, [rawInvoiceItems, returnedQtyByKey]);

  // Default selection: all remaining items when modal opens / invoiceItems change
  useEffect(() => {
    if (!open) return;

    if (!invoiceItems.length) {
      setSelectedIndexes([]);
      return;
    }

    setSelectedIndexes(invoiceItems.map((_, idx) => idx));
  }, [open, invoiceId, invoiceItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default mode / charges / note when modal opens
  useEffect(() => {
    if (!open) return;

    setMode("Cash");
    setCharges("");

    const inv = effectiveInvoice || {};
    setNote(
      `Sales return against Invoice ${
        inv.sourceNo || inv.invoiceNo || inv.invNo || inv.invCode || ""
      }`
    );
  }, [open, effectiveInvoice]);

  const handleChargesChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setCharges("");
      return;
    }
    let num = parseFloat(raw);
    if (isNaN(num) || num < 0) {
      setCharges("");
      return;
    }
    setCharges(String(num));
  };

  const toggleIndex = (idx) => {
    setSelectedIndexes((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleFullReturn = () => {
    setSelectedIndexes(invoiceItems.map((_, idx) => idx));
  };

  const handleClearSelection = () => {
    setSelectedIndexes([]);
  };

  const itemsCount = invoiceItems.length;

  const selectedItems = useMemo(
    () => invoiceItems.filter((_, idx) => selectedIndexes.includes(idx)),
    [invoiceItems, selectedIndexes]
  );

  const selectedItemsTotal = useMemo(
    () =>
      selectedItems.reduce((sum, it) => {
        const amt = Number(it.totalItemAmount ?? it.itemAmount ?? 0) || 0;
        return sum + amt;
      }, 0),
    [selectedItems]
  );

  const chargesNum = Number(charges) || 0;
  const computedRefundAmount = Math.max(0, selectedItemsTotal - chargesNum);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ SAFETY: block if not fully paid
    if (!isFullyPaid) {
      toast.error("Sales return is allowed only for fully paid invoices.");
      return;
    }

    if (!selectedItems.length) {
      toast.error("At least one item must be selected for return.");
      return;
    }

    if (computedRefundAmount <= 0) {
      toast.error("Refund amount must be greater than zero.");
      return;
    }

    try {
      const inv = effectiveInvoice || {};

      const payload = {
        invoiceId: inv._id || invoiceId,
        invoiceCode:
          inv.invCode || inv.invoiceNo || inv.sourceNo || inv.invNo || "",
        customerId: inv.customerId?._id || inv.customerId || customer?.value || null,
        branchId: inv.branchId?._id || inv.branchId || null,

        srDate: new Date().toISOString(),
        notes: note || "",

        // 👇 Backend yahi fields use karega stock / totals ke liye
        stockEntries: selectedItems,
        looseStones: [],
        looseDiamonds: [],

        // Return charges ko discount ke tarah treat karenge (deduction)
        totalDiscount: String(chargesNum),
        courierCharge: "0",
        gstRoundOff: false,

        // Extra info (optional)
        refundMode: mode,
        returnAmount: String(computedRefundAmount),
        returnCharges: String(chargesNum),
      };

      const res = await addSalesReturn(payload).unwrap();

      toast.success("Sales return created successfully.");
      onCreated?.(res);
      onClose?.();
    } catch (err) {
      console.error("addSalesReturn error", err);
      toast.error(err?.data?.message || "Failed to create sales return. Try again.");
    }
  };

  const invoiceDate =
    effectiveInvoice.invDate ||
    effectiveInvoice.date ||
    effectiveInvoice.createdAt ||
    null;

  return (
    <AnimatePresence>
      {open && invoiceId && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl border border-slate-100 p-4 flex flex-col gap-3 max-h-[92vh]"
            initial={{ y: 30, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 230, damping: 22 }}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <FaUndo className="text-base" />
                </div>
                <div className="flex flex-col">
                  <h2 className="text-sm font-semibold text-slate-900">
                    Create Sales Return
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    Sales return is allowed only for <span className="font-semibold text-slate-800">fully paid</span> invoices.
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Invoice:{" "}
                    <span className="font-medium text-slate-800">
                      {effectiveInvoice.sourceNo ||
                        effectiveInvoice.invoiceNo ||
                        effectiveInvoice.invNo ||
                        effectiveInvoice.invCode ||
                        "-"}
                    </span>{" "}
                    · Date: {formatDate(invoiceDate)}
                  </p>
                  {customer?.label && (
                    <p className="text-[10px] text-slate-500">
                      Customer:{" "}
                      <span className="font-medium text-slate-800">
                        {customer.label}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                type="button"
                disabled={isSaving}
              >
                Close
              </button>
            </div>

            {/* Invoice summary stats */}
            <div className="grid grid-cols-3 gap-3 text-[11px]">
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5">
                <p className="text-[10px] text-slate-500">Invoice Amount</p>
                <p className="mt-1 text-base font-semibold text-slate-900">
                  ₹ {formatAmount(grandTotal)}
                </p>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-2.5">
                <p className="text-[10px] text-emerald-600">Paid</p>
                <p className="mt-1 text-base font-semibold text-emerald-900">
                  ₹ {formatAmount(paidTotal)}
                </p>
              </div>
              <div className="rounded-xl bg-rose-50 border border-rose-100 p-2.5">
                <p className="text-[10px] text-rose-600">Outstanding</p>
                <p className="mt-1 text-base font-semibold text-rose-900">
                  ₹ {formatAmount(outstanding)}
                </p>
              </div>
            </div>

            {/* Fully paid rule warning */}
            {!isFullyPaid && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
                Sales Return is blocked because this invoice is <b>not fully paid</b>.
                Please clear outstanding amount first.
              </div>
            )}

            {/* Items list for partial / full return */}
            <div className="flex flex-col gap-2 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-slate-600">
                  Invoice Items{" "}
                  <span className="text-slate-400">
                    ({itemsCount} item{itemsCount === 1 ? "" : "s"})
                  </span>
                  <span className="ml-2 text-[10px] text-slate-400">
                    (Fully returned items hidden)
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    className="px-2.5 py-1 rounded-full border border-slate-200 text-[10px] text-slate-600 hover:bg-slate-50"
                    disabled={isFetchingInvoice || !itemsCount || !isFullyPaid}
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    onClick={handleFullReturn}
                    className="px-2.5 py-1 rounded-full border border-amber-200 text-[10px] text-amber-700 bg-amber-50 hover:bg-amber-100"
                    disabled={isFetchingInvoice || !itemsCount || !isFullyPaid}
                  >
                    Full return (select all)
                  </button>
                </div>
              </div>

              <div className="border border-slate-100 rounded-xl max-h-48 overflow-auto">
                {isFetchingInvoice ? (
                  <div className="flex items-center justify-center py-6 text-[11px] text-slate-500">
                    <Spinner size={14} className="mr-2" />
                    Loading invoice items...
                  </div>
                ) : !itemsCount ? (
                  <div className="flex items-center justify-center py-6 text-[11px] text-slate-500">
                    No remaining items found for this invoice.
                  </div>
                ) : (
                  <table className="min-w-full text-[10px]">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600">
                        <th className="px-2 py-1 text-left w-6">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={
                              selectedIndexes.length === itemsCount && itemsCount > 0
                            }
                            onChange={(e) =>
                              e.target.checked
                                ? handleFullReturn()
                                : handleClearSelection()
                            }
                            disabled={!isFullyPaid}
                          />
                        </th>
                        <th className="px-2 py-1 text-left">Item</th>
                        <th className="px-2 py-1 text-right">Net Wt (g)</th>
                        <th className="px-2 py-1 text-right">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceItems.map((row, idx) => {
                        const isSelected = selectedIndexes.includes(idx);
                        return (
                          <tr
                            key={row._id || row.invoiceItemCode || row.itemCode || idx}
                            className={isSelected ? "bg-amber-50/60" : "hover:bg-slate-50"}
                          >
                            <td className="px-2 py-1 align-middle">
                              <input
                                type="checkbox"
                                className="h-3 w-3"
                                checked={isSelected}
                                onChange={() => toggleIndex(idx)}
                                disabled={!isFullyPaid}
                              />
                            </td>
                            <td className="px-2 py-1 align-middle">
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-800">
                                  {row.itemCode || row.invoiceItemCode || "-"}
                                </span>
                                <span className="text-[9px] text-slate-500">
                                  {[row.categoryName, row.productName, row.designName, row.purityName]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              </div>
                            </td>
                            <td className="px-2 py-1 text-right align-middle text-slate-700">
                              {Number(row.netWt || row.grossWt || 0).toFixed(3)}
                            </td>
                            <td className="px-2 py-1 text-right align-middle text-slate-900 font-medium">
                              {formatAmount(row.totalItemAmount ?? row.itemAmount ?? 0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex justify-end text-[10px] text-slate-600">
                Selected items total:{" "}
                <span className="ml-1 font-semibold text-slate-900">
                  ₹ {formatAmount(selectedItemsTotal)}
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 text-[11px]">
              <div className="grid grid-cols-2 gap-3">
                {/* Return amount (auto) */}
                <div>
                  <label className="block font-medium text-slate-700 mb-1">
                    Return Amount (auto)
                  </label>
                  <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 bg-slate-50">
                    <FaRupeeSign className="text-[10px] text-slate-400" />
                    <input
                      type="text"
                      className="w-full border-none outline-none text-[12px] text-slate-900 bg-transparent"
                      value={formatAmount(computedRefundAmount)}
                      readOnly
                    />
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    Sum of selected item amounts minus return charges.
                  </p>
                </div>

                {/* Refund mode */}
                <div>
                  <label className="block font-medium text-slate-700 mb-1">
                    Refund Mode
                  </label>
                  <select
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={!isFullyPaid}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                    <option value="RTGS">RTGS</option>
                    <option value="Wallet">Wallet (credit to wallet)</option>
                    <option value="Adjust">Adjust in ledger only</option>
                  </select>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    This is only for your record; ledger posting will be handled by backend.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Return charges */}
                <div>
                  <label className="block font-medium text-slate-700 mb-1">
                    Return Charges (if any)
                  </label>
                  <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white">
                    <FaRupeeSign className="text-[10px] text-slate-400" />
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border-none outline-none text-[12px] text-slate-900 placeholder-slate-400"
                      value={charges}
                      onChange={handleChargesChange}
                      placeholder="0.00"
                      disabled={!isFullyPaid}
                    />
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    Any deduction like making charges, damage, etc. (will reduce refund amount).
                  </p>
                </div>

                {/* Note */}
                <div>
                  <label className="block font-medium text-slate-700 mb-1">
                    Note / Reason
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Example: Customer returned ring, wrong size."
                    disabled={!isFullyPaid}
                  />
                </div>
              </div>

              {/* Footer buttons */}
              <div className="flex justify-between items-center pt-2 border-t border-slate-100 text-[11px]">
                <div className="text-slate-500">
                  This sales return will be linked to the invoice and appear in the customer ledger as an{" "}
                  <span className="font-semibold text-amber-700">Invoice Return</span>.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-[11px]"
                    onClick={onClose}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      isSaving ||
                      isFetchingInvoice ||
                      !isFullyPaid ||
                      !selectedItems.length ||
                      computedRefundAmount <= 0
                    }
                    className="px-4 py-1.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-2 shadow-sm bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                  >
                    {isSaving && <Spinner size={12} />}
                    Create Sales Return
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateSalesReturnModal;
